/**
 * Notifications Service - AccountsGenie
 * Service for managing notification data and API interactions
 */

import type {
  Notification,
  NotificationResponse,
  NotificationFilters,
  NotificationPreferences,
  NotificationStats
} from '../types/notifications';

const API_BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Sample notification data for testing
 */
const generateSampleNotifications = (): Notification[] => {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  return [
    {
      id: '1',
      type: 'payment',
      priority: 'high',
      title: 'Payment Overdue',
      message: 'Invoice #INV-001 is 5 days overdue. Please follow up with the client.',
      isRead: false,
      createdAt: new Date(now.getTime() - 2 * 60 * 60 * 1000), // 2 hours ago
      actionUrl: '/reports?filter=overdue',
      actionText: 'View Invoice'
    },
    {
      id: '2',
      type: 'success',
      priority: 'medium',
      title: 'Report Generated',
      message: 'Your monthly profit & loss report has been successfully generated.',
      isRead: false,
      createdAt: new Date(now.getTime() - 4 * 60 * 60 * 1000), // 4 hours ago
      actionUrl: '/reports/monthly-pl',
      actionText: 'View Report'
    },
    {
      id: '3',
      type: 'system',
      priority: 'low',
      title: 'System Update',
      message: 'AccountsGenie has been updated to version 2.1.0 with new features.',
      isRead: true,
      createdAt: yesterday,
      readAt: new Date(yesterday.getTime() + 60 * 60 * 1000), // 1 hour after creation
      actionUrl: '/settings/changelog',
      actionText: 'View Changes'
    },
    {
      id: '4',
      type: 'warning',
      priority: 'medium',
      title: 'Backup Reminder',
      message: 'Your last data backup was 7 days ago. Consider creating a new backup.',
      isRead: true,
      createdAt: new Date(yesterday.getTime() - 12 * 60 * 60 * 1000), // 12 hours before yesterday
      readAt: yesterday,
      actionUrl: '/settings/backup',
      actionText: 'Create Backup'
    },
    {
      id: '5',
      type: 'info',
      priority: 'low',
      title: 'Feature Tip',
      message: 'Did you know you can export reports to multiple formats? Check out the export options.',
      isRead: false,
      createdAt: lastWeek,
      actionUrl: '/reports',
      actionText: 'Try Now'
    },
    {
      id: '6',
      type: 'error',
      priority: 'critical',
      title: 'Connection Issue',
      message: 'Unable to sync with your bank account. Please check your connection settings.',
      isRead: false,
      createdAt: new Date(now.getTime() - 30 * 60 * 1000), // 30 minutes ago
      actionUrl: '/settings/connections',
      actionText: 'Fix Connection'
    }
  ];
};

/**
 * Generate notification stats from notifications array
 */
const generateStats = (notifications: Notification[]): NotificationStats => {
  const stats: NotificationStats = {
    total: notifications.length,
    unread: notifications.filter(n => !n.isRead).length,
    byType: {
      info: 0,
      success: 0,
      warning: 0,
      error: 0,
      system: 0,
      payment: 0,
      report: 0
    },
    byPriority: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    }
  };

  notifications.forEach(notification => {
    stats.byType[notification.type]++;
    stats.byPriority[notification.priority]++;
  });

  return stats;
};

/**
 * Filter notifications based on provided filters
 */
const filterNotifications = (notifications: Notification[], filters: NotificationFilters = {}): Notification[] => {
  return notifications.filter(notification => {
    if (filters.type && notification.type !== filters.type) return false;
    if (filters.priority && notification.priority !== filters.priority) return false;
    if (filters.isRead !== undefined && notification.isRead !== filters.isRead) return false;
    if (filters.dateFrom && notification.createdAt < filters.dateFrom) return false;
    if (filters.dateTo && notification.createdAt > filters.dateTo) return false;
    return true;
  });
};

/**
 * Default notification preferences
 */
const defaultPreferences: NotificationPreferences = {
  enableSound: true,
  enableBrowserNotifications: true,
  enableEmailNotifications: false,
  types: {
    info: true,
    success: true,
    warning: true,
    error: true,
    system: true,
    payment: true,
    report: true
  },
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '08:00'
  }
};

/**
 * Notifications Service Class
 */
class NotificationsService {
  private sampleNotifications: Notification[] = generateSampleNotifications();

  /**
   * Fetch notifications with optional filtering and pagination
   */
  async fetchNotifications(
    filters: NotificationFilters = {},
    cursor?: string,
    limit: number = 50
  ): Promise<NotificationResponse> {
    try {
      // In development, use sample data
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
        
        const filteredNotifications = filterNotifications(this.sampleNotifications, filters);
        const sortedNotifications = filteredNotifications.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        return {
          notifications: sortedNotifications.slice(0, limit),
          stats: generateStats(this.sampleNotifications),
          hasMore: sortedNotifications.length > limit,
          nextCursor: sortedNotifications.length > limit ? 'next-page' : undefined
        };
      }

      // Production API call
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.isRead !== undefined) params.append('isRead', filters.isRead.toString());
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
      if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
      if (cursor) params.append('cursor', cursor);
      params.append('limit', limit.toString());

      const response = await fetch(`${API_BASE_URL}/notifications?${params}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<void> {
    try {
      // In development, update sample data
      if (import.meta.env.DEV) {
        const notification = this.sampleNotifications.find(n => n.id === id);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          notification.readAt = new Date();
        }
        return;
      }

      // Production API call
      const response = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notification as read: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark notification as unread
   */
  async markAsUnread(id: string): Promise<void> {
    try {
      // In development, update sample data
      if (import.meta.env.DEV) {
        const notification = this.sampleNotifications.find(n => n.id === id);
        if (notification && notification.isRead) {
          notification.isRead = false;
          notification.readAt = undefined;
        }
        return;
      }

      // Production API call
      const response = await fetch(`${API_BASE_URL}/notifications/${id}/unread`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notification as unread: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      // In development, update sample data
      if (import.meta.env.DEV) {
        const now = new Date();
        this.sampleNotifications.forEach(notification => {
          if (!notification.isRead) {
            notification.isRead = true;
            notification.readAt = now;
          }
        });
        return;
      }

      // Production API call
      const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to mark all notifications as read: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(id: string): Promise<void> {
    try {
      // In development, update sample data
      if (import.meta.env.DEV) {
        const index = this.sampleNotifications.findIndex(n => n.id === id);
        if (index > -1) {
          this.sampleNotifications.splice(index, 1);
        }
        return;
      }

      // Production API call
      const response = await fetch(`${API_BASE_URL}/notifications/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete notification: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Delete all read notifications
   */
  async deleteAllRead(): Promise<void> {
    try {
      // In development, update sample data
      if (import.meta.env.DEV) {
        this.sampleNotifications = this.sampleNotifications.filter(n => !n.isRead);
        return;
      }

      // Production API call
      const response = await fetch(`${API_BASE_URL}/notifications/read`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete read notifications: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting read notifications:', error);
      throw error;
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<NotificationPreferences> {
    try {
      // In development, return default preferences
      if (import.meta.env.DEV) {
        const savedPreferences = localStorage.getItem('notification-preferences');
        if (savedPreferences) {
          return { ...defaultPreferences, ...JSON.parse(savedPreferences) };
        }
        return defaultPreferences;
      }

      // Production API call
      const response = await fetch(`${API_BASE_URL}/notifications/preferences`);
      if (!response.ok) {
        throw new Error(`Failed to fetch preferences: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      return defaultPreferences;
    }
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(preferences: Partial<NotificationPreferences>): Promise<void> {
    try {
      // In development, save to localStorage
      if (import.meta.env.DEV) {
        const currentPreferences = await this.getPreferences();
        const updatedPreferences = { ...currentPreferences, ...preferences };
        localStorage.setItem('notification-preferences', JSON.stringify(updatedPreferences));
        return;
      }

      // Production API call
      const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences)
      });

      if (!response.ok) {
        throw new Error(`Failed to update preferences: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }

  /**
   * Show browser notification
   */
  showBrowserNotification(notification: Notification): void {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const options: NotificationOptions = {
      body: notification.message,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: notification.id,
      data: {
        id: notification.id,
        actionUrl: notification.actionUrl
      }
    };

    const browserNotification = new Notification(notification.title, options);

    browserNotification.onclick = () => {
      window.focus();
      if (notification.actionUrl) {
        window.location.href = notification.actionUrl;
      }
      browserNotification.close();
    };

    // Auto-close after 5 seconds
    setTimeout(() => {
      browserNotification.close();
    }, 5000);
  }
}

// Export singleton instance
export const notificationsService = new NotificationsService();
export default notificationsService;